/** @format */

import { trackDataLayer } from 'src/utils/dataLayer';
import { isAnalyticsConsentGranted } from 'src/analytics/analyticsConsent';
import { buildGa4EventPayload } from 'src/analytics/buildGa4EventPayload';
import { GA4_EVENT_CATALOG, isGa4EventKey } from 'src/analytics/ga4EventCatalog';
import { pickGa4Params } from 'src/analytics/ga4Parameters';
import type { Ga4BaseParams } from 'src/analytics/ga4Parameters';
import type { Ga4UserContext } from 'src/analytics/ga4UserContext';

export type Ga4EventParams = Record<string, unknown>;

export interface TrackGa4Options {
  pageContext?: Ga4BaseParams | Record<string, unknown>;
  userContext?: Ga4UserContext | Record<string, unknown>;
  mergeBase?: boolean;
}

/**
 * Pushes a GA4 catalog event to GTM dataLayer when analytics consent is granted.
 */
export function trackGa4(
  eventKey: string,
  eventParams: object = {},
  options: TrackGa4Options = {}
): void {
  if (!isGa4EventKey(eventKey)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[trackGa4] Unknown event key: ${eventKey}`);
    }
    return;
  }

  if (!isAnalyticsConsentGranted()) {
    // Vite sets NODE_ENV to "development" for local serve; keep tracking/logs usable without the cookie banner.
    if (process.env.NODE_ENV === 'development') {
      console.debug(
        '[trackGa4] Analytics consent not granted; allowing event in development:',
        eventKey
      );
    } else {
      return;
    }
  }

  const { ga4EventName } = GA4_EVENT_CATALOG[eventKey];
  const { pageContext, userContext, mergeBase = true } = options;
  const hasContext = Boolean(pageContext || userContext);
  const paramsRecord = eventParams as Record<string, unknown>;

  const pickedParams = hasContext
    ? buildGa4EventPayload(eventKey, {
        eventParams: paramsRecord,
        pageContext: pageContext as Ga4BaseParams,
        userContext: userContext as { user_status?: string; user_id?: string },
        includeBase: mergeBase
      })
    : pickGa4Params(eventKey, { event_name: eventKey, ...paramsRecord });

  const payload = {
    ...pickedParams,
    event_name: eventKey
  };

  if (process.env.NODE_ENV === 'development') {
    console.debug('[trackGa4]', ga4EventName, JSON.stringify(payload, null, 2));
  }

  trackDataLayer(ga4EventName, payload);
}
